import { Injectable } from '@angular/core';

import { select, Store, Action } from '@ngrx/store';

import * as fromChat from './chat.reducer';
import * as ChatSelectors from './chat.selectors';
import * as ChatActions from './chat.actions';
import { ConnectionState, ICustomerUser } from '@care-portals/api-interfaces';

@Injectable()
export class ChatFacade {
  isInitialized$ = this.store.pipe(select(ChatSelectors.getInitialized));
  loading$ = this.store.pipe(select(ChatSelectors.getChatLoading));
  activeChat$ = this.store.pipe(select(ChatSelectors.getActiveChat));
  activeChatCount$ = this.store.pipe(select(ChatSelectors.getActiveChatCount));
  chatState$ = this.store.pipe(select(ChatSelectors.getChatState));
  identity$ = this.store.pipe(select(ChatSelectors.getIdentity));
  messages$ = this.store.pipe(select(ChatSelectors.getMessages));
  isSidebarOpen$ = this.store.pipe(select(ChatSelectors.isSidebarOpen));
  waiting = false;
  isInitialized: boolean;
  filters: any;

  activeCustomer$ = this.store.pipe(select(ChatSelectors.activeCustomer));

  mode$ = this.store.pipe(select(ChatSelectors.getMode));

  /** Id of the chat session currently in the store — see ChatActions.closeChat. */
  sessionId$ = this.store.pipe(select(ChatSelectors.getSessionId));

  constructor(private store: Store<fromChat.State>) {}

  dispatch(action: Action) {
    this.store.dispatch(action);
  }

  /**
   * Single chokepoint for opening chat. A customer without an id would put
   * null into activeCustomer and render the chat body's empty state, so it
   * never reaches the store — callers that need to wait should defer instead.
   */
  openChat(customer: ICustomerUser, mode?: 'inbox' | 'chat', style: 'drawer' | 'sidebar' = 'sidebar') {
    if (!customer?._id) return;
    this.dispatch(ChatActions.openChat({ customer, mode, style }));
  }
  startEncounter() {
    this.dispatch(ChatActions.startChat());
  }

  refreshToken() {
    this.dispatch(ChatActions.refreshToken());
  }

  onConnectionStateChange(state: ConnectionState) {
    this.dispatch(
      ChatActions.connectionStateChanged({ connectionState: state })
    );
  }

  /**
   * Pass the session id the caller opened/mounted with so a superseded view
   * cannot tear down a chat that has since been replaced by a newer one.
   * Omit it to act on whatever session is current.
   */
  removeParticipant(sessionId?: number) {
    this.dispatch(ChatActions.removeParticipant({ sessionId }));
  }

  closeChat(sessionId?: number) {
    this.dispatch(ChatActions.closeChat({ sessionId }));
  }

  sendMessage(chatId: string, message: string, files: any[]) {
    this.dispatch(ChatActions.sendMessage({ chatId, message, files }));
  }

  loadMore(chatId: string) {
    this.dispatch(ChatActions.loadMore({ chatId }));
  }

  forwardCS(customerId: string) {
    this.dispatch(ChatActions.forwardCS({ customerId }));
  }

  forwardPH(customerId: string) {
    this.dispatch(ChatActions.forwardPH({ customerId }));
  }

  sendPing(customerId: string) {
    this.dispatch(ChatActions.ping({ customerId }));
  }

  joinVideo(customerId: string) {
    this.dispatch(ChatActions.joinVideo({ customerId }));
  }

}
